import React, { useEffect, useState } from "react";
import LoggerService from "../services/LoggerService";
import {
  AlertTriangle,
  HardDrive,
  Gauge,
  Cpu,
  RefreshCw,
  Network,
  Database,
  Users,
  Activity,
  CheckCircle,
  XCircle,
} from "lucide-react";

const ResourceMonitor = () => {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchResources = async () => {
    try {
      const data = await LoggerService.getSystemResources();
      setResources(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError("Failed to fetch resource data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkResources = async () => {
    try {
      await LoggerService.checkResources();
    } catch (err) {
      console.error("Failed to check resources:", err);
    }
  };

  const handleManualRefresh = async () => {
    setLoading(true);
    await fetchResources();
    await checkResources();
  };

  useEffect(() => {
    fetchResources();
    checkResources();

    const interval = setInterval(fetchResources, 30000);
    return () => clearInterval(interval);
  }, []);

  const getColorClass = (percentage) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getTextColorClass = (percentage) => {
    if (percentage >= 90) return "text-red-500";
    if (percentage >= 70) return "text-yellow-500";
    return "text-green-500";
  };

  const getMeetingLoadColor = (count) => {
    if (count >= 12) return "text-red-500";
    if (count >= 8) return "text-yellow-500";
    return "text-green-500";
  };

  const getMeetingLoadBgColor = (count) => {
    if (count >= 12) return "bg-red-500";
    if (count >= 8) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px] w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[300px] w-full text-red-500 p-4 text-center">
        <div className="flex flex-col items-center space-y-2">
          <AlertTriangle className="h-8 w-8" />
          <p>{error}</p>
          <button
            onClick={handleManualRefresh}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">
          System Resources Monitor
        </h2>
        <button
          onClick={handleManualRefresh}
          className="flex items-center space-x-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Infrastructure Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        {/* Disk Usage Card */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <HardDrive className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            <h3 className="text-base sm:text-lg font-semibold">Disk Usage</h3>
          </div>
          <div className="relative h-4 bg-gray-200 rounded-full mb-2">
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
                resources?.diskUsage?.usagePercentage
              )}`}
              style={{ width: `${resources?.diskUsage?.usagePercentage}%` }}
            ></div>
          </div>
          <div
            className={`text-right font-bold ${getTextColorClass(
              resources?.diskUsage?.usagePercentage
            )}`}
          >
            {resources?.diskUsage?.usagePercentage.toFixed(1)}%
          </div>
          <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
            <div className="flex justify-between">
              <span>Total:</span>
              <span>
                {(
                  resources?.diskUsage?.totalBytes /
                  (1024 * 1024 * 1024)
                ).toFixed(2)}{" "}
                GB
              </span>
            </div>
            <div className="flex justify-between">
              <span>Free:</span>
              <span>
                {(
                  resources?.diskUsage?.freeBytes /
                  (1024 * 1024 * 1024)
                ).toFixed(2)}{" "}
                GB
              </span>
            </div>
          </div>
        </div>

        {/* Memory Usage Card */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Gauge className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            <h3 className="text-base sm:text-lg font-semibold">Memory Usage</h3>
          </div>
          <div className="relative h-4 bg-gray-200 rounded-full mb-2">
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
                resources?.memory?.usagePercentage
              )}`}
              style={{ width: `${resources?.memory?.usagePercentage}%` }}
            ></div>
          </div>
          <div
            className={`text-right font-bold ${getTextColorClass(
              resources?.memory?.usagePercentage
            )}`}
          >
            {resources?.memory?.usagePercentage.toFixed(1)}%
          </div>
          <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
            <div className="flex justify-between">
              <span>Total:</span>
              <span>
                {(resources?.memory?.totalBytes / (1024 * 1024 * 1024)).toFixed(
                  2
                )}{" "}
                GB
              </span>
            </div>
            <div className="flex justify-between">
              <span>Free:</span>
              <span>
                {(resources?.memory?.freeBytes / (1024 * 1024 * 1024)).toFixed(
                  2
                )}{" "}
                GB
              </span>
            </div>
          </div>
        </div>

        {/* CPU Usage Card */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Cpu className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            <h3 className="text-base sm:text-lg font-semibold">CPU Usage</h3>
          </div>
          <div className="relative h-4 bg-gray-200 rounded-full mb-2">
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
                resources?.cpu?.usagePercentage
              )}`}
              style={{ width: `${resources?.cpu?.usagePercentage}%` }}
            ></div>
          </div>
          <div
            className={`text-right font-bold ${getTextColorClass(
              resources?.cpu?.usagePercentage
            )}`}
          >
            {resources?.cpu?.usagePercentage.toFixed(1)}%
          </div>
          <div className="mt-2 text-xs sm:text-sm text-gray-600">
            System CPU utilization
          </div>
        </div>
      </div>

      {/* NEW: Application Metrics Row */}
      {resources?.redis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {/* Redis Status Card */}
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex items-center mb-4">
              <Database className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              <h3 className="text-base sm:text-lg font-semibold">Redis Status</h3>
            </div>
            <div className="flex items-center mb-3">
              {resources.redis.isConnected ? (
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mr-2" />
              )}
              <span
                className={`font-semibold ${
                  resources.redis.isConnected ? "text-green-600" : "text-red-600"
                }`}
              >
                {resources.redis.isConnected ? "Connected" : "Disconnected"}
              </span>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Memory Usage:</span>
                <span>{resources.redis.memoryUsageMB.toFixed(1)} MB</span>
              </div>
              <div className="flex justify-between">
                <span>Total Keys:</span>
                <span>{resources.redis.totalKeys}</span>
              </div>
              <div className="flex justify-between">
                <span>Active Meetings:</span>
                <span className={getMeetingLoadColor(resources.redis.activeMeetings)}>
                  {resources.redis.activeMeetings}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Connection: {resources.redis.connectionString}
              </div>
            </div>
          </div>

          {/* Meeting Metrics Card */}
          {resources?.meetings && (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
              <div className="flex items-center mb-4">
                <Users className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                <h3 className="text-base sm:text-lg font-semibold">Meeting Metrics</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Concurrent Meetings:</span>
                  <span className={`font-bold text-lg ${getMeetingLoadColor(resources.meetings.concurrentMeetings)}`}>
                    {resources.meetings.concurrentMeetings}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Active Bots:</span>
                  <span className="font-semibold text-blue-600">
                    {resources.meetings.activeBots}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Recordings:</span>
                  <span className="text-gray-700">
                    {resources.meetings.totalRecordings}
                  </span>
                </div>
                
                {/* Meeting Load Indicator */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Meeting Load</span>
                    <span>{resources.meetings.concurrentMeetings}/15 capacity</span>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full">
                    <div
                      className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getMeetingLoadBgColor(
                        resources.meetings.concurrentMeetings
                      )}`}
                      style={{ 
                        width: `${Math.min((resources.meetings.concurrentMeetings / 15) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* System Health Summary Card */}
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex items-center mb-4">
              <Activity className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              <h3 className="text-base sm:text-lg font-semibold">System Health</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Infrastructure:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  resources?.memory?.usagePercentage < 70 && 
                  resources?.cpu?.usagePercentage < 80 && 
                  resources?.diskUsage?.usagePercentage < 75
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}>
                  {resources?.memory?.usagePercentage < 70 && 
                   resources?.cpu?.usagePercentage < 80 && 
                   resources?.diskUsage?.usagePercentage < 75 ? "Healthy" : "Warning"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Redis:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  resources?.redis?.isConnected
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {resources?.redis?.isConnected ? "Online" : "Offline"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Meeting Load:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  (resources?.meetings?.concurrentMeetings || 0) < 8
                    ? "bg-green-100 text-green-800"
                    : (resources?.meetings?.concurrentMeetings || 0) < 12
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {(resources?.meetings?.concurrentMeetings || 0) < 8 ? "Low" : 
                   (resources?.meetings?.concurrentMeetings || 0) < 12 ? "Medium" : "High"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Network Usage Card */}
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
        <div className="flex items-center mb-4">
          <Network className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
          <h3 className="text-base sm:text-lg font-semibold">Network Usage</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-xs sm:text-sm text-gray-600">
            <div className="font-medium mb-1">Inbound:</div>
            <div className="text-lg font-semibold text-blue-600">
              {resources?.network?.inboundKBps.toFixed(2)} KB/s
            </div>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            <div className="font-medium mb-1">Outbound:</div>
            <div className="text-lg font-semibold text-green-600">
              {resources?.network?.outboundKBps.toFixed(2)} KB/s
            </div>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            <div className="font-medium mb-1">Total Received:</div>
            <div className="text-sm">
              {(resources?.network?.totalBytesReceived / (1024 * 1024)).toFixed(2)} MB
            </div>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            <div className="font-medium mb-1">Total Transmitted:</div>
            <div className="text-sm">
              {(resources?.network?.totalBytesTransmitted / (1024 * 1024)).toFixed(2)} MB
            </div>
          </div>
        </div>
      </div>

      {/* CPU Cores Card */}
      {resources?.cpu?.coreUsages && (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex items-center mb-4">
            <Cpu className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            <h3 className="text-base sm:text-lg font-semibold">
              CPU Cores ({resources.cpu.coreCount})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(resources.cpu.coreUsages).map(([core, usage]) => (
              <div key={core}>
                <div className="flex justify-between text-sm mb-1">
                  <span>Core {core}</span>
                  <span className={getTextColorClass(usage)}>
                    {usage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 bg-gray-200 rounded-full">
                  <div
                    className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
                      usage
                    )}`}
                    style={{ width: `${usage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 text-xs sm:text-sm text-gray-500 flex flex-col sm:flex-row sm:justify-between items-start sm:items-center">
        <p>Last updated: {lastUpdated.toLocaleString()}</p>
        <p>Auto-refreshes every 30 seconds</p>
      </div>
    </div>
  );
};

export default ResourceMonitor;

// import React, { useEffect, useState } from "react";
// import LoggerService from "../services/LoggerService";
// import {
//   AlertTriangle,
//   HardDrive,
//   Gauge,
//   Cpu,
//   RefreshCw,
//   Network,
// } from "lucide-react";

// const ResourceMonitor = () => {
//   const [resources, setResources] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [lastUpdated, setLastUpdated] = useState(new Date());

//   const fetchResources = async () => {
//     try {
//       const data = await LoggerService.getSystemResources();
//       setResources(data);
//       setLastUpdated(new Date());
//       setError(null);
//     } catch (err) {
//       setError("Failed to fetch resource data");
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const checkResources = async () => {
//     try {
//       await LoggerService.checkResources();
//     } catch (err) {
//       console.error("Failed to check resources:", err);
//     }
//   };

//   const handleManualRefresh = async () => {
//     setLoading(true);
//     await fetchResources();
//     await checkResources();
//   };

//   useEffect(() => {
//     fetchResources();
//     checkResources();

//     const interval = setInterval(fetchResources, 30000);
//     return () => clearInterval(interval);
//   }, []);

//   const getColorClass = (percentage) => {
//     if (percentage >= 90) return "bg-red-500";
//     if (percentage >= 70) return "bg-yellow-500";
//     return "bg-green-500";
//   };

//   const getTextColorClass = (percentage) => {
//     if (percentage >= 90) return "text-red-500";
//     if (percentage >= 70) return "text-yellow-500";
//     return "text-green-500";
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center min-h-[300px] w-full">
//         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex items-center justify-center min-h-[300px] w-full text-red-500 p-4 text-center">
//         <div className="flex flex-col items-center space-y-2">
//           <AlertTriangle className="h-8 w-8" />
//           <p>{error}</p>
//           <button
//             onClick={handleManualRefresh}
//             className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
//           >
//             Try Again
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full max-w-7xl mx-auto p-4 sm:p-6">
//       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
//         <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-0">
//           System Resources Monitor
//         </h2>
//         <button
//           onClick={handleManualRefresh}
//           className="flex items-center space-x-2 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
//         >
//           <RefreshCw className="h-4 w-4" />
//           <span>Refresh</span>
//         </button>
//       </div>

//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
//         {/* Disk Usage Card */}
//         <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
//           <div className="flex items-center mb-4">
//             <HardDrive className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
//             <h3 className="text-base sm:text-lg font-semibold">Disk Usage</h3>
//           </div>
//           <div className="relative h-4 bg-gray-200 rounded-full mb-2">
//             <div
//               className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
//                 resources?.diskUsage?.usagePercentage
//               )}`}
//               style={{ width: `${resources?.diskUsage?.usagePercentage}%` }}
//             ></div>
//           </div>
//           <div
//             className={`text-right font-bold ${getTextColorClass(
//               resources?.diskUsage?.usagePercentage
//             )}`}
//           >
//             {resources?.diskUsage?.usagePercentage.toFixed(1)}%
//           </div>
//           <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
//             <div className="flex justify-between">
//               <span>Total:</span>
//               <span>
//                 {(
//                   resources?.diskUsage?.totalBytes /
//                   (1024 * 1024 * 1024)
//                 ).toFixed(2)}{" "}
//                 GB
//               </span>
//             </div>
//             <div className="flex justify-between">
//               <span>Free:</span>
//               <span>
//                 {(
//                   resources?.diskUsage?.freeBytes /
//                   (1024 * 1024 * 1024)
//                 ).toFixed(2)}{" "}
//                 GB
//               </span>
//             </div>
//           </div>
//         </div>

//         {/* Memory Usage Card */}
//         <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
//           <div className="flex items-center mb-4">
//             <Gauge className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
//             <h3 className="text-base sm:text-lg font-semibold">Memory Usage</h3>
//           </div>
//           <div className="relative h-4 bg-gray-200 rounded-full mb-2">
//             <div
//               className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
//                 resources?.memory?.usagePercentage
//               )}`}
//               style={{ width: `${resources?.memory?.usagePercentage}%` }}
//             ></div>
//           </div>
//           <div
//             className={`text-right font-bold ${getTextColorClass(
//               resources?.memory?.usagePercentage
//             )}`}
//           >
//             {resources?.memory?.usagePercentage.toFixed(1)}%
//           </div>
//           <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
//             <div className="flex justify-between">
//               <span>Total:</span>
//               <span>
//                 {(resources?.memory?.totalBytes / (1024 * 1024 * 1024)).toFixed(
//                   2
//                 )}{" "}
//                 GB
//               </span>
//             </div>
//             <div className="flex justify-between">
//               <span>Free:</span>
//               <span>
//                 {(resources?.memory?.freeBytes / (1024 * 1024 * 1024)).toFixed(
//                   2
//                 )}{" "}
//                 GB
//               </span>
//             </div>
//           </div>
//         </div>

//         {/* CPU Usage Card */}
//         <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
//           <div className="flex items-center mb-4">
//             <Cpu className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
//             <h3 className="text-base sm:text-lg font-semibold">CPU Usage</h3>
//           </div>
//           <div className="relative h-4 bg-gray-200 rounded-full mb-2">
//             <div
//               className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
//                 resources?.cpu?.usagePercentage
//               )}`}
//               style={{ width: `${resources?.cpu?.usagePercentage}%` }}
//             ></div>
//           </div>
//           <div
//             className={`text-right font-bold ${getTextColorClass(
//               resources?.cpu?.usagePercentage
//             )}`}
//           >
//             {resources?.cpu?.usagePercentage.toFixed(1)}%
//           </div>
//           <div className="mt-2 text-xs sm:text-sm text-gray-600">
//             System CPU utilization
//           </div>
//         </div>
//       </div>

//       {/* Network Usage Card */}
//       <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
//         <div className="flex items-center mb-4">
//           <Network className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
//           <h3 className="text-base sm:text-lg font-semibold">Network Usage</h3>
//         </div>
//         <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
//           <div className="flex justify-between">
//             <span>Inbound:</span>
//             <span>{resources?.network?.inboundKBps.toFixed(2)} KB/s</span>
//           </div>
//           <div className="flex justify-between">
//             <span>Outbound:</span>
//             <span>{resources?.network?.outboundKBps.toFixed(2)} KB/s</span>
//           </div>
//           <div className="flex justify-between">
//             <span>Total Received:</span>
//             <span>
//               {(resources?.network?.totalBytesReceived / (1024 * 1024)).toFixed(
//                 2
//               )}{" "}
//               MB
//             </span>
//           </div>
//           <div className="flex justify-between">
//             <span>Total Transmitted:</span>
//             <span>
//               {(
//                 resources?.network?.totalBytesTransmitted /
//                 (1024 * 1024)
//               ).toFixed(2)}{" "}
//               MB
//             </span>
//           </div>
//         </div>
//       </div>

//       {/* CPU Cores Card */}
//       {resources?.cpu?.coreUsages && (
//         <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
//           <div className="flex items-center mb-4">
//             <Cpu className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
//             <h3 className="text-base sm:text-lg font-semibold">
//               CPU Cores ({resources.cpu.coreCount})
//             </h3>
//           </div>
//           <div className="space-y-2">
//             {Object.entries(resources.cpu.coreUsages).map(([core, usage]) => (
//               <div key={core}>
//                 <div className="flex justify-between text-sm mb-1">
//                   <span>Core {core}</span>
//                   <span className={getTextColorClass(usage)}>
//                     {usage.toFixed(1)}%
//                   </span>
//                 </div>
//                 <div className="relative h-2 bg-gray-200 rounded-full">
//                   <div
//                     className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${getColorClass(
//                       usage
//                     )}`}
//                     style={{ width: `${usage}%` }}
//                   ></div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       <div className="mt-6 text-xs sm:text-sm text-gray-500 flex flex-col sm:flex-row sm:justify-between items-start sm:items-center">
//         <p>Last updated: {lastUpdated.toLocaleString()}</p>
//         <p>Auto-refreshes every 30 seconds</p>
//       </div>
//     </div>
//   );
// };

// export default ResourceMonitor;
